import React, { useState } from 'react';
import { Auth, API } from 'aws-amplify';
import { getCurrentAuthenticatedUserDeduped } from '../utils';
import './CloseAccountModal.css';

/**
 * Close Account Modal Component
 * Handles account closure with MFA verification and acknowledgment
 */
const CloseAccountModal = ({ isOpen, onClose, accountData, onClosureComplete }) => {
  const [step, setStep] = useState('warning'); // 'warning', 'mfa', 'processing', 'complete', 'error'
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaError, setMfaError] = useState('');
  const [closureReason, setClosureReason] = useState('');
  const [acknowledgeIrreversible, setAcknowledgeIrreversible] = useState(false);
  const [acknowledgeDataLoss, setAcknowledgeDataLoss] = useState(false);
  const [acknowledgeNoRefund, setAcknowledgeNoRefund] = useState(false);
  const [acknowledgeTerms, setAcknowledgeTerms] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [closurePayload, setClosurePayload] = useState(null);

  if (!isOpen) return null;

  const handleClose = () => {
    // Reset state
    setStep('warning');
    setPassword('');
    setMfaCode('');
    setMfaError('');
    setClosureReason('');
    setAcknowledgeIrreversible(false);
    setAcknowledgeDataLoss(false);
    setAcknowledgeNoRefund(false);
    setAcknowledgeTerms(false);
    setErrorMessage('');
    setIsProcessing(false);
    setClosurePayload(null);
    onClose();
  };

  const proceedToMFA = () => {
    // Validate acknowledgments
    if (!acknowledgeIrreversible || !acknowledgeDataLoss || !acknowledgeNoRefund || !acknowledgeTerms) {
      setErrorMessage('You must acknowledge all statements to proceed');
      return;
    }
    
    if (!closureReason.trim()) {
      setErrorMessage('Please provide a reason for closure');
      return;
    }

    setErrorMessage('');
    setStep('mfa');
  };

  const verifyMFAAndClose = async () => {
    if (!accountData || !accountData.account_number) {
      setMfaError('Account number is required. Please refresh the page and try again.');
      setIsProcessing(false);
      return;
    }

    if (!mfaCode || mfaCode.length !== 6) {
      setMfaError('Please enter a valid 6-digit MFA code');
      return;
    }

    setIsProcessing(true);
    setMfaError('');

    try {
      // Get current user session, bypassing cache to ensure we have the latest state
      const user = await getCurrentAuthenticatedUserDeduped();

      // Note: the MFA code itself is intentionally NOT logged (OWASP A09).
      // Verify MFA code by confirming software token
      try {
        const username = user.username || user.attributes.email;
        const signInSession = await Auth.signIn(username, password);
        
        if (signInSession.challengeName === 'SOFTWARE_TOKEN_MFA') {
           await Auth.confirmSignIn(signInSession, mfaCode, 'SOFTWARE_TOKEN_MFA');
           console.log('MFA verification successful via step-up auth');
           
           // Wait gracefully for local cache payload rewrite before fetching token
           await new Promise(resolve => setTimeout(resolve, 200));
        } else {
           throw new Error('MFA challenge not received during step-up re-authentication.');
        }
      } catch (mfaVerifyError) {
        // Log only the error class/code, never the full Cognito error object
        // (it can carry RequestId / response body). Detailed diagnostics live
        // in CloudWatch on the backend side.
        const errName = mfaVerifyError?.name || mfaVerifyError?.code || '';
        const errMsg = String(mfaVerifyError?.message || '');
        console.error('MFA verification failed:', errName || 'unknown');
        let userFacingError;
        if (errName === 'EnableSoftwareTokenMFAException' || errMsg.toLowerCase().includes('not set up')) {
          userFacingError =
            'TOTP authenticator app is not set up for this account. ' +
            'Please sign out and complete the TOTP setup before closing your account.';
        } else if (errName === 'CodeMismatchException' || errName === 'NotAuthorizedException' || errMsg.includes('Incorrect username or password')) {
          userFacingError = 'Invalid password or MFA code. Please verify your credentials and try again.';
        } else if (errName === 'ExpiredCodeException') {
          userFacingError = 'MFA code has expired. Please generate a new code from your authenticator app.';
        } else {
          userFacingError = 'MFA verification failed. Please try again or ensure your password is correct.';
        }
        setMfaError(userFacingError);
        setIsProcessing(false);
        return;
      }

      // MFA verified - proceed with account closure
      setStep('processing');

      // Call account closure API
      // Auth.currentSession() reads from Amplify's local cache (already populated by
      // confirmSignIn above) — no network call, no 8s timeout risk.
      let freshEmail = user?.attributes?.email?.toLowerCase().trim();
      let token;
      try {
        const session = await Auth.currentSession();
        token = session.getIdToken().getJwtToken();
      } catch {
        const freshUser = await getCurrentAuthenticatedUserDeduped();
        freshEmail = freshUser?.attributes?.email?.toLowerCase().trim();
        token = freshUser?.signInUserSession?.idToken?.jwtToken;
      }
      const apiName = 'accountAppBackend';
      const path = '/close-account';
      
      const requestBody = {
        account_email: accountData.account_email,
        company_name: accountData.company_name,
        requester_email: freshEmail || accountData.requester_email?.toLowerCase().trim() || accountData.account_email?.toLowerCase().trim(),
        account_number: accountData.account_number,
        closure_reason: closureReason.trim(),
        mfa_verified: true
      };

      console.log('Submitting closure request initiated.');

      await API.post(apiName, path, {
        body: requestBody,
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      console.log('Closure request submitted successfully.');
      const payload = {
        ...accountData,
        closure_reason: closureReason.trim(),
        requester_email: user?.attributes?.email || accountData.requester_email,
        request_status: 'CLOSURE_PENDING'
      };
      setClosurePayload(payload);
      if (onClosureComplete) {
        setTimeout(() => {
          onClosureComplete(payload);
        }, 1500);
      }
      setStep('complete');

    } catch (error) {
      // Log only class name; full error stays server-side in CloudWatch.
      console.error('Account closure error:', error?.name || 'unknown');
      setStep('error');
      
      if (error.response?.data?.error) {
        setErrorMessage(error.response.data.error);
      } else if (error.message) {
        setErrorMessage(error.message);
      } else {
        setErrorMessage('Failed to process account closure. Please try again or contact support.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const renderWarningStep = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>Close Account</h2>
        <button className="close-btn" onClick={handleClose} disabled={isProcessing}>×</button>
      </div>
      
      <div className="modal-body">
        <div className="warning-box">
          <h3>Account Information</h3>
          <div className="account-details">
            <p>
              <strong>Account Number:</strong> 
              <span>
                {`******${String(accountData.account_number || '').slice(-6)}`}
              </span>
            </p>
            <p><strong>Company Name:</strong> <span>{accountData.company_name}</span></p>
            <p><strong>Account Email:</strong> <span>{accountData.account_email}</span></p>
            <p><strong>Name Prefix:</strong> <span>{accountData.name_prefix}</span></p>
          </div>
        </div>

        <div className="danger-box">
          <h3>CRITICAL WARNING — Account Closure is IRREVERSIBLE</h3>
          <p>Please read and understand the following:</p>
          <ul>
            <li><strong>Data Archive &amp; Deletion</strong> - A final data export archive will be generated and provided via a download link. However, your active AppStream environment and all non-exported data will be permanently deleted.</li>
            <li><strong>Infrastructure Destruction</strong> - All associated AWS infrastructure (AppStream fleets, CloudFormation stacks, and underlying resources) will be completely destroyed.</li>
            <li><strong>Access Revoked</strong> - Your user account will be disabled and all AppStream access will be immediately revoked.</li>
            <li><strong>Irreversible Process</strong> - Once initiated, the closure workflow cannot be stopped, paused, or reversed. Your account will ultimately be moved to a closed account Organizational Unit.</li>
          </ul>
        </div>

        <div className="form-group">
          <label htmlFor="closureReason">Reason for Closure (Required):</label>
          <textarea
            id="closureReason"
            value={closureReason}
            onChange={(e) => setClosureReason(e.target.value)}
            placeholder="Please explain why you are closing this account..."
            rows="4"
            maxLength="500"
            disabled={isProcessing}
          />
          <small>{closureReason.length}/500 characters</small>
        </div>

        <div className="acknowledgments">
          <h4>Required Acknowledgments:</h4>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={acknowledgeIrreversible}
              onChange={(e) => setAcknowledgeIrreversible(e.target.checked)}
              disabled={isProcessing}
            />
            <span>I understand that account closure is <strong>IRREVERSIBLE</strong> and cannot be undone</span>
          </label>
          
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={acknowledgeDataLoss}
              onChange={(e) => setAcknowledgeDataLoss(e.target.checked)}
              disabled={isProcessing}
            />
            <span>I understand that my active environment will be destroyed, and only exported data will be retained via a final archive link</span>
          </label>
          
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={acknowledgeNoRefund}
              onChange={(e) => setAcknowledgeNoRefund(e.target.checked)}
              disabled={isProcessing}
            />
            <span>I understand that <strong>Service Charges are non-refundable</strong>, and any eligible unused Ceiling Balance will be calculated against final usage and automatically refunded starting 24 hours after closure</span>
          </label>

          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={acknowledgeTerms}
              onChange={(e) => setAcknowledgeTerms(e.target.checked)}
              disabled={isProcessing}
            />
            <span>I have read and agree to the <a href="/terms-of-service" target="_blank" rel="noopener noreferrer"><strong>Terms of Service</strong></a> regarding account closure</span>
          </label>

          <div className="support-notice">
            If you have any questions or are unsure about closing your account, <strong>stop here and contact support</strong> before proceeding.
          </div>
        </div>

        {errorMessage && (
          <div className="error-message">
            {errorMessage}
          </div>
        )}
      </div>

      <div className="modal-footer">
        <button className="btn-cancel" onClick={handleClose} disabled={isProcessing}>
          Cancel
        </button>
        <button 
          className="btn-danger" 
          onClick={proceedToMFA}
          disabled={isProcessing || !acknowledgeIrreversible || !acknowledgeDataLoss || !acknowledgeNoRefund || !acknowledgeTerms || !closureReason.trim()}
        >
          Proceed to MFA Verification
        </button>
      </div>
    </div>
  );

  const renderMFAStep = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>MFA Verification Required</h2>
        <button className="close-btn" onClick={handleClose} disabled={isProcessing}>×</button>
      </div>
      
      <div className="modal-body">
        <div className="info-box">
          <p>For security, please re-enter your password and verify your identity using Multi-Factor Authentication (MFA) before closing your account.</p>
        </div>

        <div className="form-group">
          <label htmlFor="password">Enter your password:</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setMfaError('');
            }}
            placeholder="Password"
            className="mfa-input form-control"
            disabled={isProcessing}
            autoFocus
          />
        </div>

        <div className="form-group" style={{ marginTop: '15px' }}>
          <label htmlFor="mfaCode">Enter 6-digit code from your authenticator app:</label>
          <input
            type="text"
            id="mfaCode"
            value={mfaCode}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 6);
              setMfaCode(value);
              setMfaError('');
            }}
            placeholder="000000"
            maxLength="6"
            className="mfa-input form-control"
            disabled={isProcessing}
          />
        </div>

        {mfaError && (
          <div className="error-message">
            {mfaError}
          </div>
        )}

        <div className="warning-box">
          <p><strong>Final Confirmation:</strong></p>
          <p>After MFA verification, your account closure will begin immediately and cannot be stopped.</p>
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn-cancel" onClick={() => setStep('warning')} disabled={isProcessing}>
          Back
        </button>
        <button 
          className="btn-danger" 
          onClick={verifyMFAAndClose}
          disabled={isProcessing || mfaCode.length !== 6 || password.length === 0}
        >
          {isProcessing ? 'Verifying...' : 'Verify & Close Account'}
        </button>
      </div>
    </div>
  );

  const renderProcessingStep = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>Processing Account Closure</h2>
      </div>
      
      <div className="modal-body">
        <div className="processing-box">
          <div className="spinner"></div>
          <h3>Account closure in progress...</h3>
          <p>Please do not close this window. This may take several minutes.</p>
          <ul className="processing-steps">
            <li>✓ MFA verified</li>
            <li>⏳ Initiating closure process</li>
            <li>⏳ Deleting Dashboard infrastructure</li>
            <li>⏳ Deleting base infrastructure</li>
            <li>⏳ Updating account status</li>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderCompleteStep = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>✓ Account Closure Initiated</h2>
      </div>
      
      <div className="modal-body">
        <div className="success-box">
          <h3>Account closure has been successfully initiated</h3>
          <p>Your account is being closed. You will receive an email notification when the process is complete. If you have an eligible unused Ceiling Balance, it will be calculated against final usage and automatically refunded starting 24 hours after closure — you'll receive a separate email once that's finished.</p>
          <div className="account-details">
            <p><strong>Account Number:</strong> <span>{`******${String(accountData.account_number || '').slice(-6)}`}</span></p>
            <p><strong>Status:</strong> <span>CLOSURE_PENDING</span></p>
          </div>
        </div>
        <div className="info-box">
          <p>Click "View Closure Status" to track your account closure progress in real-time.</p>
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn-primary" onClick={() => {
          if (onClosureComplete && closurePayload) {
            onClosureComplete(closurePayload);
          } else {
            handleClose();
          }
        }}>
          View Closure Status
        </button>
      </div>
    </div>
  );

  const renderErrorStep = () => (
    <div className="modal-content">
      <div className="modal-header">
        <h2>Closure Error</h2>
        <button className="close-btn" onClick={handleClose}>×</button>
      </div>
      
      <div className="modal-body">
        <div className="error-box">
          <h3>Failed to process account closure</h3>
          <p>{errorMessage}</p>
        </div>
        <div className="info-box">
          <p>Please try again or contact support if the problem persists.</p>
        </div>
      </div>

      <div className="modal-footer">
        <button className="btn-cancel" onClick={handleClose}>
          Close
        </button>
        <button className="btn-primary" onClick={() => setStep('warning')}>
          Try Again
        </button>
      </div>
    </div>
  );

  return (
    <div className="modal-overlay">
      {step === 'warning' && renderWarningStep()}
      {step === 'mfa' && renderMFAStep()}
      {step === 'processing' && renderProcessingStep()}
      {step === 'complete' && renderCompleteStep()}
      {step === 'error' && renderErrorStep()}
    </div>
  );
};

export default CloseAccountModal;
